// Privacy notice service
import urls from '../config/urls';

class PrivacyService {
  
  /**
   * Get privacy notice from API
   * @param {string} institution - Institution parameter (optional)
   * @returns {Promise<Object>} Privacy notice data
   */
  async getPrivacyNotice(institution = '') {
    try {
      const url = `${urls.apiEndpoints.privacy}/institutions/aviso-privacidad?institucion=${encodeURIComponent(institution)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'accept-language': 'es-419,es;q=0.9',
          'origin': urls.app,
          'referer': urls.app,
          'sec-ch-ua': '"Not)A;Brand";v="8", "Chromium";v="138", "Google Chrome";v="138"',
          'sec-ch-ua-mobile': '?0',
          'sec-ch-ua-platform': '"macOS"',
          'sec-fetch-dest': 'empty',
          'sec-fetch-mode': 'cors',
          'sec-fetch-site': 'same-site',
          'user-agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/138.0.0.0 Safari/537.36'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Aviso de privacidad obtenido desde API');
      
      // Extraer datos de la estructura de respuesta de la API
      const privacyData = data.data || data;
      const template = privacyData.template || privacyData.contenido || privacyData.content || '';
      
      return {
        success: true,
        data: privacyData,
        title: privacyData.nombre || privacyData.titulo || 'Aviso de Privacidad',
        content: template,
        description: privacyData.descripcion || '',
        lastUpdated: privacyData.dt_editado || privacyData.fechaActualizacion || privacyData.lastUpdated || new Date().toISOString()
      };
      
    } catch (error) {
      console.error('❌ Error obteniendo aviso de privacidad:', error);
      
      // Return fallback privacy notice
      return {
        success: false,
        error: error.message,
        data: this.getFallbackPrivacyNotice(),
        title: 'Aviso de Privacidad',
        content: this.getFallbackPrivacyNotice().contenido,
        lastUpdated: new Date().toISOString()
      };
    }
  }

  /**
   * Get fallback privacy notice when API fails
   * @returns {Object} Fallback privacy notice
   */
  getFallbackPrivacyNotice() {
    return {
      titulo: 'Aviso de Privacidad',
      contenido: `
        <h3>AVISO DE PRIVACIDAD DE THINK HEALTH</h3>
        
        <p>Think Health S.A.P.I. de C.V., con domicilio en [Dirección], es responsable del tratamiento de sus datos personales, del uso que se le dé a los mismos y de su protección.</p>
        
        <h4>¿Para qué fines utilizaremos sus datos personales?</h4>
        <p>Sus datos personales serán utilizados para las siguientes finalidades:</p>
        <ul>
          <li>Proveer los servicios de la plataforma ThinkHealth</li>
          <li>Gestionar su registro y perfil de usuario</li>
          <li>Procesar pagos y facturación</li>
          <li>Brindar soporte técnico</li>
          <li>Enviar comunicaciones relacionadas con el servicio</li>
        </ul>
        
        <h4>¿Qué datos personales utilizaremos?</h4>
        <p>Para las finalidades antes mencionadas, requerimos obtener los siguientes datos personales:</p>
        <ul>
          <li>Datos de identificación: nombre, apellidos, CURP</li>
          <li>Datos de contacto: correo electrónico, teléfono</li>
          <li>Datos profesionales: formación académica, institución</li>
          <li>Datos de facturación: RFC, dirección fiscal</li>
        </ul>
        
        <h4>Derechos ARCO</h4>
        <p>Usted tiene derecho a conocer qué datos personales tenemos de usted, para qué los utilizamos y las condiciones del uso que les damos (Acceso). Asimismo, es su derecho solicitar la corrección de su información personal en caso de que esté desactualizada, sea inexacta o incompleta (Rectificación); que la eliminemos de nuestros registros o bases de datos cuando considere que la misma no está siendo utilizada adecuadamente (Cancelación); así como oponerse al uso de sus datos personales para fines específicos (Oposición).</p>
        
        <p>Para ejercer cualquiera de los derechos ARCO, deberá presentar la solicitud respectiva a través del correo electrónico: privacidad@thinkhealth.mx</p>
        
        <p><strong>Fecha de última actualización:</strong> ${new Date().toLocaleDateString('es-MX')}</p>
      `,
      fechaActualizacion: new Date().toISOString()
    };
  }

  /**
   * Format privacy notice content for display
   * @param {Object} privacyData - Privacy notice data
   * @returns {string} Formatted HTML content
   */
  formatPrivacyContent(privacyData) {
    if (!privacyData) {
      return this.getFallbackPrivacyNotice().contenido;
    }

    let content = privacyData.content || privacyData.data?.template || privacyData.data?.contenido || privacyData.data?.content || '';
    
    if (!content) {
      return this.getFallbackPrivacyNotice().contenido;
    }

    // If content is already HTML, return as is
    if (content.includes('<') && content.includes('>')) {
      return content;
    }

    // Convert plain text to HTML with proper formatting
    // Handle escaped newlines and convert to proper HTML structure
    return content
      .replace(/\\n\\n/g, '</p><p>')  // Handle escaped double newlines
      .replace(/\\n/g, '<br>')        // Handle escaped single newlines
      .replace(/\n\n/g, '</p><p>')    // Handle actual double newlines
      .replace(/\n/g, '<br>')         // Handle actual single newlines
      .replace(/^/, '<p>')            // Add opening paragraph
      .replace(/$/, '</p>')           // Add closing paragraph
      .replace(/<p><\/p>/g, '')       // Remove empty paragraphs
      .replace(/<p><br>/g, '<p>');    // Clean up paragraph starts
  }
}

const privacyService = new PrivacyService();
export default privacyService;
